<script setup lang="ts">
/**
 * Left-rail chat settings.
 *
 * Drives every knob that goes into a `/v1/chat/completions` request:
 *   - Which model to send (live from `GET /v1/models`).
 *   - Which API key to use as Bearer (live from hub's `GET /api/keys`,
 *     ACTIVE only).
 *   - Sampling: temperature, max_tokens.
 *   - System prompt.
 *   - Whether to attach the dummy `echo_value` tool definition so the
 *     model can demonstrate tool calling.
 *
 * All state lives in `v-model`s owned by the parent (ChatView) so it can
 * persist or reset the entire form in one place. Empty-state CTAs handle
 * "no models yet" and "no active keys" by linking to /keys.
 */
import { computed } from 'vue';
import type { ApiKey } from '../../api/keys';
import type { OpenAIModel } from '../../lib/openai-client';

interface Props {
  // Live data
  models: OpenAIModel[];
  modelsLoading: boolean;
  modelsError: string | null;
  keys: ApiKey[];
  keysLoading: boolean;
  // v-model targets
  selectedModel: string;
  selectedKeyId: string | null;
  temperature: number;
  maxTokens: number;
  systemPrompt: string;
  toolCallingEnabled: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:selectedModel', v: string): void;
  (e: 'update:selectedKeyId', v: string | null): void;
  (e: 'update:temperature', v: number): void;
  (e: 'update:maxTokens', v: number): void;
  (e: 'update:systemPrompt', v: string): void;
  (e: 'update:toolCallingEnabled', v: boolean): void;
  (e: 'clear'): void;
  (e: 'refreshModels'): void;
}>();

const activeKeys = computed(() => props.keys.filter((k) => !k.revoked_at));

function onModelChange(e: Event): void {
  emit('update:selectedModel', (e.target as HTMLSelectElement).value);
}
function onKeyChange(e: Event): void {
  const v = (e.target as HTMLSelectElement).value;
  emit('update:selectedKeyId', v.length > 0 ? v : null);
}
function onTempInput(e: Event): void {
  const v = Number((e.target as HTMLInputElement).value);
  if (Number.isFinite(v)) emit('update:temperature', v);
}
function onMaxTokensInput(e: Event): void {
  const v = Number((e.target as HTMLInputElement).value);
  if (Number.isFinite(v) && v > 0) emit('update:maxTokens', v);
}
function onSystemPromptInput(e: Event): void {
  emit('update:systemPrompt', (e.target as HTMLTextAreaElement).value);
}
function onToolToggle(e: Event): void {
  emit('update:toolCallingEnabled', (e.target as HTMLInputElement).checked);
}
</script>

<template>
  <aside
    class="h-full overflow-y-auto bg-panel border-r border-border p-5 flex flex-col gap-5"
    aria-label="Chat settings"
  >
    <header class="flex items-center justify-between">
      <h2 class="text-sm font-semibold uppercase tracking-wide text-muted">Settings</h2>
      <button
        type="button"
        class="text-xs text-muted hover:text-ink"
        title="Reload models"
        @click="emit('refreshModels')"
      >
        ↻
      </button>
    </header>

    <!-- Model selector -->
    <div class="flex flex-col gap-1.5">
      <label class="text-xs uppercase tracking-wide text-muted" for="chat-model">Model</label>
      <select
        id="chat-model"
        class="input"
        :value="props.selectedModel"
        :disabled="props.modelsLoading || props.models.length === 0"
        @change="onModelChange"
      >
        <option v-if="props.modelsLoading" value="">Loading…</option>
        <option v-else-if="props.models.length === 0" value="">No models</option>
        <option v-for="m in props.models" :key="m.id" :value="m.id">
          {{ m.display_name ?? m.id }}
        </option>
      </select>
      <p v-if="props.modelsError" class="text-xs text-danger">{{ props.modelsError }}</p>
    </div>

    <!-- API key selector -->
    <div class="flex flex-col gap-1.5">
      <label class="text-xs uppercase tracking-wide text-muted" for="chat-key">API key</label>
      <select
        v-if="activeKeys.length > 0"
        id="chat-key"
        class="input"
        :value="props.selectedKeyId ?? ''"
        :disabled="props.keysLoading"
        @change="onKeyChange"
      >
        <option v-for="k in activeKeys" :key="k.id" :value="k.id">
          {{ k.name ?? 'unnamed' }} ({{ k.prefix }}…{{ k.suffix }})
        </option>
      </select>
      <div
        v-else
        class="card p-3 text-xs text-muted flex flex-col gap-2"
        data-testid="no-active-keys"
      >
        <span>You have no active API keys. The chat needs a key to call the router.</span>
        <router-link to="/keys" class="btn-primary w-fit">Create a key</router-link>
      </div>
    </div>

    <!-- Temperature -->
    <div class="flex flex-col gap-1.5">
      <div class="flex items-center justify-between">
        <label class="text-xs uppercase tracking-wide text-muted" for="chat-temp">Temperature</label>
        <span class="text-xs text-ink tabular-nums">{{ props.temperature.toFixed(2) }}</span>
      </div>
      <input
        id="chat-temp"
        type="range"
        min="0"
        max="2"
        step="0.05"
        :value="props.temperature"
        @input="onTempInput"
        class="w-full accent-accent"
      />
    </div>

    <!-- Max tokens -->
    <div class="flex flex-col gap-1.5">
      <label class="text-xs uppercase tracking-wide text-muted" for="chat-maxtok">Max tokens</label>
      <input
        id="chat-maxtok"
        type="number"
        min="1"
        max="32768"
        step="1"
        :value="props.maxTokens"
        @input="onMaxTokensInput"
        class="input"
      />
    </div>

    <!-- System prompt -->
    <div class="flex flex-col gap-1.5">
      <label class="text-xs uppercase tracking-wide text-muted" for="chat-system">System prompt</label>
      <textarea
        id="chat-system"
        rows="4"
        class="input font-mono text-xs leading-relaxed"
        placeholder="You are a helpful assistant."
        :value="props.systemPrompt"
        @input="onSystemPromptInput"
      />
    </div>

    <!-- Tool calling -->
    <label class="flex items-start gap-2 cursor-pointer">
      <input
        type="checkbox"
        class="mt-1 accent-accent"
        :checked="props.toolCallingEnabled"
        @change="onToolToggle"
      />
      <span class="flex flex-col">
        <span class="text-sm">Tool calling</span>
        <span class="text-xs text-muted">
          Attaches a dummy <code>echo_value</code> tool the model can invoke.
        </span>
      </span>
    </label>

    <div class="mt-auto pt-4 border-t border-border">
      <button type="button" class="btn-ghost w-full" @click="emit('clear')">
        Clear thread
      </button>
    </div>
  </aside>
</template>
